import { useCallback, useEffect, useRef, useState } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FolderMinusIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { type EmptyFoldersScanResult, SYSTEM_FILENAMES } from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { enqueueCleanEmptyFolders, scanEmptyFolders } from './empty-folders-api';

/**
 * Modale « Nettoyer les dossiers vides » (toute la bibliothèque).
 *
 * Un dossier n'est pas un objet d'xphoto : la liste des dossiers est dérivée du chemin
 * des photos. Un dossier vidé disparaît donc tout seul de l'UI — mais le RÉPERTOIRE
 * restait sur disque. À l'ouverture, un aperçu (lecture) parcourt l'arborescence et liste
 * les répertoires sans aucun fichier ; « Supprimer » enfile le job `empty_folders_cleanup`
 * (progression suivie dans la cloche), qui n'appelle que `rmdir` — jamais `unlink`.
 *
 * Boîte de dialogue accessible : `role="dialog"` + `aria-modal`, focus initial/restauré,
 * verrou de défilement, Échap ferme. Le caller l'enveloppe dans un `<Portal>`.
 */
interface CleanEmptyFoldersModalProps {
  libraryId: string;
  onClose: () => void;
  /** Appelé après un enfilement réussi (p.ex. rafraîchir la galerie). */
  onQueued?: () => void;
}

type ScanState =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; result: EmptyFoldersScanResult };

export function CleanEmptyFoldersModal({
  libraryId,
  onClose,
  onQueued,
}: CleanEmptyFoldersModalProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const [scan, setScan] = useState<ScanState>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  useModalA11y({ dialogRef, initialFocusRef: closeRef, onEscape: !busy ? onClose : undefined });
  const [cleanError, setCleanError] = useState<'writeDisabled' | 'generic' | null>(null);
  const [done, setDone] = useState(false);
  // Effacer aussi les fichiers système : opt-in EXPLICITE, jamais coché par défaut —
  // c'est le seul chemin du produit où un fichier est supprimé (ADR-0010 amendée).
  const [removeSystemFiles, setRemoveSystemFiles] = useState(false);

  const runScan = useCallback(async () => {
    setScan({ status: 'loading' });
    try {
      const result = await scanEmptyFolders(libraryId);
      setScan({ status: 'ready', result });
    } catch {
      setScan({ status: 'error' });
    }
  }, [libraryId]);

  // Aperçu automatique à l'ouverture (lecture seule).
  useEffect(() => {
    void runScan();
  }, [runScan]);

  const onSubmit = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setCleanError(null);
    try {
      await enqueueCleanEmptyFolders(libraryId, { dryRun: false, removeSystemFiles });
      setDone(true);
      onQueued?.();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setCleanError('writeDisabled');
      } else {
        setCleanError('generic');
      }
    } finally {
      setBusy(false);
    }
  }, [busy, libraryId, onQueued, removeSystemFiles]);

  const total = scan.status === 'ready' ? scan.result.total : 0;
  const systemOnlyTotal = scan.status === 'ready' ? scan.result.systemOnlyTotal : 0;
  // Ce que le bouton va RÉELLEMENT supprimer, case à cocher comprise : afficher le total
  // des deux catégories alors qu'une seule est ciblée serait un mensonge de chiffre.
  const targeted = total + (removeSystemFiles ? systemOnlyTotal : 0);
  const canClean = scan.status === 'ready' && targeted > 0 && !done;

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('cleanEmptyFolders.title')}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        {/* En-tête. */}
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <FolderMinusIcon className="h-6 w-6 text-brand" aria-hidden="true" />
            {t('cleanEmptyFolders.title')}
          </h2>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            disabled={busy}
            title={t('cleanEmptyFolders.close')}
            aria-label={t('cleanEmptyFolders.close')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Corps. */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          {done ? (
            <p
              role="status"
              className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-fg"
            >
              <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
              {t('cleanEmptyFolders.queued')}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted">{t('cleanEmptyFolders.intro')}</p>
              <p className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-muted">
                <InformationCircleIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-brand"
                  aria-hidden="true"
                />
                {t('cleanEmptyFolders.note')}
              </p>

              {scan.status === 'loading' && (
                <p role="status" className="inline-flex items-center gap-2 text-sm text-muted">
                  <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
                  {t('cleanEmptyFolders.scanning')}
                </p>
              )}

              {scan.status === 'error' && (
                <div className="flex flex-col gap-2">
                  <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
                    <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {t('cleanEmptyFolders.scanError')}
                  </p>
                  <button
                    type="button"
                    onClick={() => void runScan()}
                    className="self-start rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    {t('cleanEmptyFolders.retry')}
                  </button>
                </div>
              )}

              {scan.status === 'ready' && total === 0 && systemOnlyTotal === 0 && (
                <p className="rounded-md bg-surface-2 p-3 text-sm text-fg">
                  {t('cleanEmptyFolders.none')}
                </p>
              )}

              {scan.status === 'ready' && total > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-fg">
                    {interpolate(t('cleanEmptyFolders.count'), { count: total }, locale)}
                  </p>
                  <ul className="max-h-56 divide-y divide-border overflow-y-auto rounded-md border border-border text-xs">
                    {scan.result.sample.map((path) => (
                      <li key={path} className="px-3 py-1.5">
                        <span className="block truncate text-muted" title={path}>
                          {path}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {total > scan.result.sample.length && (
                    <p className="text-xs text-muted">
                      {interpolate(
                        t('cleanEmptyFolders.andMore'),
                        { count: total - scan.result.sample.length },
                        locale,
                      )}
                    </p>
                  )}
                </div>
              )}

              {/* Seconde catégorie : dossiers « vides à l'œil » mais porteurs d'un fichier
                  système. Montrés MÊME décochés — sans cela, l'utilisateur se demanderait
                  pourquoi un dossier qu'il croit vide ne figure nulle part. */}
              {scan.status === 'ready' && systemOnlyTotal > 0 && (
                <div className="flex flex-col gap-2 rounded-md border border-border p-3">
                  <p className="text-sm font-medium text-fg">
                    {interpolate(
                      t('cleanEmptyFolders.systemCount'),
                      { count: systemOnlyTotal },
                      locale,
                    )}
                  </p>
                  <p className="text-xs text-muted">
                    {interpolate(t('cleanEmptyFolders.systemNote'), {
                      names: SYSTEM_FILENAMES.join(', '),
                    })}
                  </p>
                  <ul className="max-h-40 divide-y divide-border overflow-y-auto rounded-md border border-border text-xs">
                    {scan.result.systemOnlySample.map((path) => (
                      <li key={path} className="px-3 py-1.5">
                        <span className="block truncate text-muted" title={path}>
                          {path}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {systemOnlyTotal > scan.result.systemOnlySample.length && (
                    <p className="text-xs text-muted">
                      {interpolate(
                        t('cleanEmptyFolders.andMore'),
                        { count: systemOnlyTotal - scan.result.systemOnlySample.length },
                        locale,
                      )}
                    </p>
                  )}
                  <label className="flex items-start gap-2 text-sm text-fg">
                    <input
                      type="checkbox"
                      checked={removeSystemFiles}
                      onChange={(e) => setRemoveSystemFiles(e.target.checked)}
                      disabled={busy}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-strong accent-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                    />
                    <span>{t('cleanEmptyFolders.systemOptIn')}</span>
                  </label>
                </div>
              )}

              {cleanError !== null && (
                <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {cleanError === 'writeDisabled'
                    ? t('cleanEmptyFolders.errorWriteDisabled')
                    : t('cleanEmptyFolders.error')}
                </p>
              )}
            </>
          )}
        </div>

        {/* Actions. */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          {done || !canClean ? (
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
            >
              {t('cleanEmptyFolders.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              >
                {t('cleanEmptyFolders.cancel')}
              </button>
              <button
                type="button"
                ref={confirmRef}
                onClick={() => void onSubmit()}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy && <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />}
                {busy
                  ? t('cleanEmptyFolders.submitting')
                  : interpolate(t('cleanEmptyFolders.confirm'), { count: targeted }, locale)}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
